/**
 * The campaign module's Template field.
 *
 * The dropdown itself now lives in `PickerSelect` — same markup, lifted out so
 * other surfaces (the ads drawer's Campaign / Ad Set fields) can wear the same
 * style without a second copy of it. This file keeps what is specific to
 * templates: the category pill on each row, and searching by category as well
 * as by name.
 */
import { useMemo } from 'react';
import { PickerSelect, type PickerSelectOption } from '@/components/ui/picker-select';
import { TemplateCategoryPill } from '@/components/marketing/templateCategoryPill';

export interface TemplatePickerTemplate {
  id: string;
  name: string;
  category?: string;
}

export function TemplatePicker({
  templates,
  selectedId,
  onSelect,
  placeholder = 'Select template',
  disabled = false,
  triggerTestId = 'campaign-template-trigger',
  searchTestId = 'campaign-template-search',
}: {
  templates: TemplatePickerTemplate[];
  selectedId: string;
  onSelect: (id: string) => void;
  placeholder?: string;
  disabled?: boolean;
  triggerTestId?: string;
  searchTestId?: string;
}) {
  const options: PickerSelectOption[] = useMemo(
    () =>
      templates.map((t) => {
        const cat = (t.category || 'MARKETING').toUpperCase();
        return {
          id: t.id,
          name: t.name,
          // Searching "marketing" has always matched the pill, not just the name.
          keywords: [t.category || ''],
          meta: <TemplateCategoryPill category={cat} />,
        };
      }),
    [templates],
  );

  return (
    <PickerSelect
      options={options}
      selectedId={selectedId}
      onSelect={onSelect}
      placeholder={placeholder}
      searchPlaceholder="Search templates…"
      emptyMessage="No templates found."
      disabled={disabled}
      triggerTestId={triggerTestId}
      searchTestId={searchTestId}
    />
  );
}
